import React, { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useStore } from "@/context/StoreContext";
import { captureEmail } from "@/lib/api";

const PRIZES = [
  { label: "10% OFF", code: "LVX10", w: 26, msg: "10% off your first fit" },
  { label: "FREE SHIP", code: "LVXSHIP", w: 22, msg: "Free shipping unlocked" },
  { label: "15% OFF", code: "LVX15", w: 10, msg: "15% off — big win" },
  { label: "+100 PTS", code: null, w: 20, msg: "100 reward points added" },
  { label: "5% OFF", code: "LVX5", w: 18, msg: "5% off your order" },
  { label: "MYSTERY", code: "LVXGIFT", w: 4, msg: "A mystery gift at checkout" },
];

const pick = () => {
  const total = PRIZES.reduce((s, p) => s + p.w, 0);
  let r = Math.random() * total;
  for (let i = 0; i < PRIZES.length; i++) { r -= PRIZES[i].w; if (r <= 0) return i; }
  return 0;
};

export const SpinToWin = () => {
  const { earn } = useStore();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    if (localStorage.getItem("lvx_spin")) return;
    const blocked = () => /\/product\//.test(window.location.pathname) || window.location.pathname === "/rewards";
    const t = setTimeout(() => { if (!blocked()) setOpen(true); }, 16000);
    const onLeave = (e) => { if (e.clientY <= 0 && !localStorage.getItem("lvx_spin") && !blocked()) setOpen(true); };
    document.addEventListener("mouseleave", onLeave);
    return () => { clearTimeout(t); document.removeEventListener("mouseleave", onLeave); };
  }, []);

  const dismiss = () => { setOpen(false); localStorage.setItem("lvx_spin", "dismissed"); };

  const spin = async () => {
    if (spinning || result) return;
    if (!email) { toast.error("Enter your email to spin"); return; }
    try { await captureEmail({ email, source: "spin" }); } catch {}
    setSpinning(true);
    const idx = pick();
    setRotation(360 * 6 + (360 - (idx * 60 + 30)));
    timer.current = setTimeout(async () => {
      setResult(PRIZES[idx]);
      setSpinning(false);
      await earn("signup");
      localStorage.setItem("lvx_spin", "won");
      toast.success(`You won ${PRIZES[idx].label}!`);
    }, 4300);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="spin-to-win">
      <motion.div initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                  className="relative bg-[#0A0A0A] text-[#F4F2ED] max-w-3xl w-full overflow-hidden border border-white/10">
        <button onClick={dismiss} data-testid="spin-close" className="absolute top-4 right-4 z-20 text-white/60 hover:text-white"><X size={20} /></button>
        <div className="relative z-10 grid sm:grid-cols-2">
          <div className="flex items-center justify-center p-10 bg-[#EDEDED]">
            <div className="relative w-[270px] h-[270px]">
              <div className="absolute left-1/2 -top-1 -translate-x-1/2 z-20"
                   style={{ width: 0, height: 0, borderLeft: "11px solid transparent", borderRight: "11px solid transparent", borderTop: "18px solid #0A0A0A" }} />
              <div className="absolute inset-0 rounded-full border-[3px] border-[#0A0A0A] shadow-xl overflow-hidden"
                   style={{
                     transition: "transform 4.2s cubic-bezier(0.17,0.67,0.2,1)",
                     transform: `rotate(${rotation}deg)`,
                     background: "conic-gradient(#0A0A0A 0 60deg,#FFFFFF 60deg 120deg,#0A0A0A 120deg 180deg,#FFFFFF 180deg 240deg,#0A0A0A 240deg 300deg,#FFFFFF 300deg 360deg)",
                   }}>
                {PRIZES.map((p, i) => (
                  <div key={i} className="absolute left-1/2 top-1/2" style={{ transform: `rotate(${i * 60 + 30}deg)` }}>
                    <span className="absolute font-display text-[10px] font-bold uppercase tracking-wider whitespace-nowrap"
                          style={{ transform: "translate(-50%,-104px)", color: i % 2 === 0 ? "#FFFFFF" : "#0A0A0A" }}>{p.label}</span>
                  </div>
                ))}
              </div>
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-[#0A0A0A] border-2 border-white z-10" />
            </div>
          </div>

          <div className="p-8 sm:p-10 flex flex-col justify-center">
            {result ? (
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="spin-result">
                <p className="overline text-white/50">You won</p>
                <h2 className="font-hero text-5xl mt-2 leading-none">{result.label}</h2>
                <p className="font-body text-white/70 mt-4">{result.msg}. {result.code ? "Use your code at checkout:" : "Points are in your account."}</p>
                {result.code && (
                  <div className="mt-4 border border-dashed border-white/40 px-4 py-3 flex items-center justify-between">
                    <span className="font-mono text-lg tracking-widest">{result.code}</span>
                    <button data-testid="spin-copy" onClick={() => { navigator.clipboard?.writeText(result.code); toast.success("Code copied"); }} className="overline">Copy</button>
                  </div>
                )}
                <button onClick={() => setOpen(false)} className="btn-fill bg-[#F4F2ED] text-[#0A0A0A] hover:bg-white w-full mt-6">Shop the Drop</button>
              </motion.div>
            ) : (
              <>
                <p className="overline text-white/50">Don't leave empty-handed</p>
                <h2 className="font-hero text-4xl sm:text-5xl mt-2 leading-none">SPIN TO<br />WIN</h2>
                <p className="font-body text-white/60 mt-4">One spin. Guaranteed prize. Drop your email and take your shot at up to 15% off + points.</p>
                <input data-testid="spin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                       placeholder="your@email.com"
                       className="mt-6 bg-transparent border border-white/30 px-4 py-3.5 outline-none font-body placeholder:text-white/40 focus:border-white transition-colors" />
                <button onClick={spin} disabled={spinning} data-testid="spin-btn" className="btn-fill bg-[#F4F2ED] text-[#0A0A0A] hover:bg-white w-full mt-3 disabled:opacity-60">
                  {spinning ? "Spinning…" : "Spin the Wheel"}
                </button>
                <button onClick={dismiss} className="font-mono text-[11px] text-white/30 mt-4 hover:text-white/50">No thanks, I'll pay full price</button>
              </>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};
